// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import GraphicsPackageLevelsTab from './GraphicsPackageLevelsTab';
import GraphicsPackageTypesManagementTab from './GraphicsPackageTypesManagementTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Package, Edit, Trash2, Search, Save, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface GraphicsPackageType {
  id: string;
  name: string;
  description?: string;
  package_type: string;
  price: number;
  is_active: boolean;
  vendor_id: string;
  vendor_name: string;
  created_at: string;
}

const PACKAGE_TYPES = [
  'Commitment',
  'Offer',
  'Visit',
  'Special Announcement',
  'Top School Lists',
  'Transfer',
  'Other'
] as const;

const GraphicsPackageTypesTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState('levels');
  const [packages, setPackages] = useState<GraphicsPackageType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state for creating/editing
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    package_type: '',
    price: '',
    vendor_id: '',
    is_active: true
  });

  const [vendors, setVendors] = useState<{id: string, name: string}[]>([]);

  useEffect(() => {
    fetchPackageTypes();
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      // Fetch vendor profiles first
      const { data: vendorProfiles, error: vendorError } = await (supabase as any)
        .from('graphics_vendor_profiles')
        .select('id, business_name, user_id')
        .eq('is_active', true);

      if (vendorError) throw vendorError;

      // Then fetch profile data for names
      const userIds = vendorProfiles?.map(v => v.user_id).filter(Boolean) || [];
      let profilesData = [];
      
      if (userIds.length > 0) {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('user_id, name')
          .in('user_id', userIds);
        
        if (!profileError) {
          profilesData = profiles || [];
        }
      }

      // Create profile map
      const profileMap = new Map(profilesData.map(p => [p.user_id, p]));

      const vendorOptions = vendorProfiles?.map(vendor => ({
        id: vendor.id,
        name: vendor.business_name || profileMap.get(vendor.user_id)?.name || 'Unknown Vendor'
      })) || [];

      setVendors(vendorOptions);
    } catch (error: any) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchPackageTypes = async () => {
    try {
      setIsLoading(true);

      // Fetch packages first
      const { data: packagesData, error: packagesError } = await (supabase as any)
        .from('graphics_packages')
        .select('*')
        .order('created_at', { ascending: false });

      if (packagesError) throw packagesError;

      if (!packagesData || packagesData.length === 0) {
        setPackages([]);
        return;
      }

      // Get vendor IDs
      const vendorIds = packagesData.map(pkg => pkg.vendor_id).filter(Boolean);
      
      // Fetch vendor profiles
      const { data: vendorProfiles } = await (supabase as any)
        .from('graphics_vendor_profiles')
        .select('id, business_name, user_id')
        .in('id', vendorIds);

      // Fetch profile names for vendors
      const userIds = vendorProfiles?.map(v => v.user_id).filter(Boolean) || [];
      let profilesData = [];
      
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, name')
          .in('user_id', userIds);
        
        profilesData = profiles || [];
      }

      // Create maps for lookups
      const profileMap = new Map(profilesData.map(p => [p.user_id, p]));
      const vendorMap = new Map(vendorProfiles?.map(v => [v.id, v]) || []);

      const enrichedPackages = packagesData.map(pkg => ({
        ...pkg,
        vendor_name: (() => {
          const vendor = vendorMap.get(pkg.vendor_id);
          if (!vendor) return 'Unknown Vendor';
          return vendor.business_name || profileMap.get(vendor.user_id)?.name || 'Unknown Vendor';
        })()
      }));

      setPackages(enrichedPackages as any);
    } catch (error: any) {
      console.error('Error fetching package types:', error);
      toast.error(error.message || 'Failed to load package types');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      package_type: '',
      price: '',
      vendor_id: '',
      is_active: true
    });
  };

  const handleCreatePackage = async () => {
    try {
      setIsSubmitting(true);

      if (!formData.name.trim() || !formData.package_type || !formData.vendor_id || !formData.price) {
        toast.error('Please fill in all required fields');
        return;
      }

      const price = parseFloat(formData.price);
      if (isNaN(price) || price < 0) {
        toast.error('Please enter a valid price');
        return;
      }

      const { error } = await (supabase as any)
        .from('graphics_packages')
        .insert([{
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          package_type: formData.package_type,
          price: price,
          vendor_id: formData.vendor_id,
          is_active: formData.is_active
        }]);

      if (error) throw error;

      toast.success('Package type created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchPackageTypes();
    } catch (error: any) {
      console.error('Error creating package type:', error);
      toast.error(error.message || 'Failed to create package type');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePackage = async (packageId: string, updates: Partial<GraphicsPackageType>) => {
    try {
      const { error } = await (supabase as any)
        .from('graphics_packages')
        .update(updates)
        .eq('id', packageId);

      if (error) throw error;

      setPackages(prevPackages =>
        prevPackages.map(pkg =>
          pkg.id === packageId ? { ...pkg, ...updates } : pkg
        )
      );

      toast.success('Package updated successfully');
    } catch (error: any) {
      console.error('Error updating package:', error);
      toast.error(error.message || 'Failed to update package');
    }
  };

  const handleDeletePackage = async (packageId: string) => {
    if (!confirm('Are you sure you want to delete this package? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await (supabase as any)
        .from('graphics_packages')
        .delete()
        .eq('id', packageId);

      if (error) throw error;

      setPackages(prevPackages => prevPackages.filter(pkg => pkg.id !== packageId));
      toast.success('Package deleted successfully');
    } catch (error: any) {
      console.error('Error deleting package:', error);
      toast.error(error.message || 'Failed to delete package');
    }
  };

  const filteredPackages = packages.filter(pkg =>
    pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pkg.package_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pkg.vendor_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const packageTypeStats = PACKAGE_TYPES.reduce((stats, type) => {
    stats[type] = packages.filter(pkg => pkg.package_type === type).length;
    return stats;
  }, {} as Record<string, number>);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading package types...</p>
        </div>
      </div>
    );
  }

  const PackageListTab: React.FC<{ packages: any[], vendors: any[], onPackageUpdated: () => void }> = ({ 
    packages, 
    vendors, 
    onPackageUpdated 
  }) => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {PACKAGE_TYPES.map(type => (
          <Card key={type}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{type}</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{packageTypeStats[type]}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Create */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Graphics Package Types ({filteredPackages.length})</CardTitle>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search packages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogTrigger asChild>
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Package Type
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[525px]">
                  <DialogHeader>
                    <DialogTitle>Create New Package Type</DialogTitle>
                    <DialogDescription>
                      Add a new graphics package type for vendors to offer.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Package Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                        placeholder="e.g., Basic Commitment Package"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="package_type">Package Type *</Label>
                      <Select 
                        value={formData.package_type} 
                        onValueChange={(value) => setFormData(prev => ({...prev, package_type: value}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select package type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PACKAGE_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vendor">Vendor *</Label>
                      <Select 
                        value={formData.vendor_id} 
                        onValueChange={(value) => setFormData(prev => ({...prev, vendor_id: value}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select vendor" />
                        </SelectTrigger>
                        <SelectContent>
                          {vendors.map(vendor => (
                            <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="price">Price *</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={(e) => setFormData(prev => ({...prev, price: e.target.value}))}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                        placeholder="Describe what's included in this package..."
                        rows={3}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData(prev => ({...prev, is_active: checked}))}
                      />
                      <Label htmlFor="is_active">Active</Label>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreatePackage} disabled={isSubmitting}>
                      {isSubmitting ? 'Creating...' : 'Create Package'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredPackages.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'No package types found matching your search.' : 'No package types found.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Package Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPackages.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell className="font-medium">{pkg.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{pkg.package_type}</Badge>
                    </TableCell>
                    <TableCell>{pkg.vendor_name}</TableCell>
                    <TableCell>${pkg.price.toFixed(2)}</TableCell>
                    <TableCell>
                      <Switch
                        checked={pkg.is_active}
                        onCheckedChange={(checked) => handleUpdatePackage(pkg.id, { is_active: checked })}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(pkg.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeletePackage(pkg.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const VendorOfferingsTab: React.FC<{ packages: any[], vendors: any[] }> = ({ packages, vendors }) => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Vendor Package Offerings</h3>
        <p className="text-sm text-muted-foreground">
          View which vendors offer which package combinations
        </p>
      </div>
      <div className="text-muted-foreground">
        Vendor offerings management coming soon...
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Package Types Management
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage graphics package levels, types, and vendor offerings
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="levels">Package Levels</TabsTrigger>
          <TabsTrigger value="types">Package Types</TabsTrigger>
          <TabsTrigger value="packages">Package List</TabsTrigger>
          <TabsTrigger value="vendors">Vendor Offerings</TabsTrigger>
        </TabsList>

        <TabsContent value="levels" className="mt-6">
          <GraphicsPackageLevelsTab />
        </TabsContent>

        <TabsContent value="types" className="mt-6">
          <GraphicsPackageTypesManagementTab />
        </TabsContent>

        <TabsContent value="packages" className="mt-6">
          <PackageListTab 
            packages={packages}
            vendors={vendors}
            onPackageUpdated={fetchPackageTypes}
          />
        </TabsContent>
        
        <TabsContent value="vendors" className="mt-6">
          <VendorOfferingsTab 
            packages={packages}
            vendors={vendors}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GraphicsPackageTypesTab;